import { createServerFn } from "@tanstack/react-start";
import { setResponseHeaders, getRequest } from "@tanstack/react-start/server";
import * as React from "react";
import { render } from "@react-email/components";
import { z } from "zod";
import { logger } from "@/lib/logger";

const ADMIN_EMAIL = "ahmity@gmail.com";
const DAILY_ALERT_THRESHOLD = 50;

function clientIp(): string {
  try {
    const req = getRequest();
    const xff = req.headers.get("x-forwarded-for");
    if (xff) return xff.split(",")[0]?.trim() ?? "unknown";
    return (
      req.headers.get("cf-connecting-ip") ??
      req.headers.get("x-real-ip") ??
      "unknown"
    );
  } catch {
    return "unknown";
  }
}

/**
 * Sets Cloudflare-edge cache headers for the public homepage.
 * Called from the `/` route loader. The page has no user-specific data,
 * so we serve a cached copy for 5 minutes — 10k visitors becomes 1 origin hit.
 */
export const cacheHomepage = createServerFn({ method: "GET" }).handler(async () => {
  setResponseHeaders(
    new Headers({
      "Cache-Control": "public, max-age=0, s-maxage=300, stale-while-revalidate=600",
      "CDN-Cache-Control": "public, max-age=300, stale-while-revalidate=600",
    }),
  );
  return { cached: true };
});

/**
 * Pre-signup rate limit. Max 5 signup attempts per IP per hour.
 * Uses the existing `check_rate_limit` Postgres RPC (atomic, cross-isolate).
 * Fails OPEN on DB error to avoid blocking real users during outages.
 */
export const checkSignupRateLimit = createServerFn({ method: "POST" }).handler(
  async () => {
    const ip = clientIp();
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data, error } = await supabaseAdmin.rpc("check_rate_limit", {
        _bucket: `signup:${ip}`,
        _max: 5,
        _window_seconds: 3600,
      });
      if (error || !Array.isArray(data) || data.length === 0) {
        return { allowed: true, remaining: 5 };
      }
      const row = data[0] as { allowed: boolean; remaining: number };
      return {
        allowed: !!row.allowed,
        remaining: Number(row.remaining ?? 0),
      };
    } catch (err) {
      logger.error("[signup-rate-limit] failed open:", err);
      return { allowed: true, remaining: 5 };
    }
  },
);

async function sendTelegram(message: string): Promise<void> {
  const token = process.env.TELEGRAM_BOT_TOKEN;
  const chatId = process.env.TELEGRAM_CHAT_ID;
  if (!token || !chatId) return;
  try {
    await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chat_id: chatId, text: message, parse_mode: "HTML" }),
    });
  } catch (err) {
    logger.error("[viral-alert] telegram send failed:", err);
  }
}

async function sendAdminEmailAlert(count: number, date: string): Promise<void> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { template } = await import("@/lib/email-templates/admin-viral-alert");

    const templateData = { count, date };
    const element = React.createElement(template.component, templateData);
    const html = await render(element);
    const plainText = await render(element, { plainText: true });
    const subject =
      typeof template.subject === "function"
        ? template.subject(templateData)
        : template.subject;

    const messageId = `viral-alert-${date}`;
    const recipient = ADMIN_EMAIL.toLowerCase();

    await supabaseAdmin.from("email_send_log").insert({
      message_id: messageId,
      template_name: "admin-viral-alert",
      recipient_email: recipient,
      status: "pending",
    });

    const { error } = await supabaseAdmin.rpc("enqueue_email", {
      queue_name: "transactional_emails",
      payload: {
        message_id: messageId,
        to: recipient,
        from: `forex-mind-signals <noreply@forexmind.ai>`,
        sender_domain: "notify.forexmind.ai",
        subject,
        html,
        text: plainText,
        purpose: "transactional",
        label: "admin-viral-alert",
        idempotency_key: messageId,
        unsubscribe_token: null,
        queued_at: new Date().toISOString(),
      },
    });
    if (error) logger.error("[viral-alert] admin email enqueue failed:", error);
  } catch (err) {
    logger.error("[viral-alert] admin email send failed:", err);
  }
}

/**
 * Called after every successful signup. Atomically increments today's
 * signup counter in `site_settings.daily_signups` and fires an admin
 * alert (Telegram + email to ADMIN_EMAIL) the first time today's count
 * crosses the threshold.
 */
export const recordSignupForAlerting = createServerFn({ method: "POST" }).handler(
  async () => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const today = new Date().toISOString().slice(0, 10);

      const { data: existing } = await supabaseAdmin
        .from("site_settings")
        .select("value")
        .eq("key", "daily_signups")
        .maybeSingle();

      const prev = (existing?.value ?? {}) as {
        date?: string;
        count?: number;
        alerted?: boolean;
      };
      const sameDay = prev.date === today;
      const nextCount = (sameDay ? Number(prev.count ?? 0) : 0) + 1;
      const alertedAlready = sameDay && !!prev.alerted;
      const shouldAlert = !alertedAlready && nextCount >= DAILY_ALERT_THRESHOLD;

      await supabaseAdmin.from("site_settings").upsert(
        {
          key: "daily_signups",
          value: { date: today, count: nextCount, alerted: shouldAlert || alertedAlready },
        },
        { onConflict: "key" },
      );

      if (shouldAlert) {
        await Promise.all([
          sendTelegram(
            `🚨 <b>High traffic on ForexMind AI</b>\n${nextCount} signups today (${today}).\n` +
              `Admin: ${ADMIN_EMAIL}\nConsider scaling the backend instance immediately.`,
          ),
          sendAdminEmailAlert(nextCount, today),
        ]);
      }

      return { count: nextCount, alerted: shouldAlert };
    } catch (err) {
      logger.error("[viral-alert] record failed:", err);
      return { count: 0, alerted: false };
    }
  },
);
